import type { ScoreTableRow, GeneratorParams } from '../types';

const RESULT_MIN_CLAMP = 0;
const RESULT_MAX_CLAMP = 9_999_999;

/**
 * Client-side implementation of the D10 piecewise-linear generator for preview.
 * Mirrors orchestra-api/src/scoring.ts generateScoreTableRows exactly.
 */
export function generateScoreTableRows(params: GeneratorParams): ScoreTableRow[] {
  if (['sex', 'age_min', 'age_max'].some(field => Object.prototype.hasOwnProperty.call(params, field))) {
    throw new Error('Age- or sex-specific scoring is prohibited. Remove sex, age_min, and age_max.');
  }
  const { max_points, min_points, top_result, bottom_result } = params;
  const range = top_result - bottom_result;
  if (range === 0) throw new Error('top_result and bottom_result must differ');

  const lo = Math.min(top_result, bottom_result);
  const hi = Math.max(top_result, bottom_result);

  const p25_result = params.p25_result ?? bottom_result + range * 0.25;
  const p75_result = params.p75_result ?? bottom_result + range * 0.75;

  const perf_p25 = (p25_result - bottom_result) / range;
  const perf_p75 = (p75_result - bottom_result) / range;

  const p25_pts = min_points + 0.375 * (max_points - min_points);
  const p75_pts = min_points + 0.625 * (max_points - min_points);

  function pointsFor(r: number): number {
    const perf = (r - bottom_result) / range;
    if (perf <= 0) return min_points;
    if (perf >= 1) return max_points;
    if (perf <= perf_p25) {
      return Math.round(min_points + (p25_pts - min_points) * (perf / perf_p25));
    }
    if (perf <= perf_p75) {
      return Math.round(
        p25_pts + (p75_pts - p25_pts) * ((perf - perf_p25) / (perf_p75 - perf_p25)),
      );
    }
    return Math.round(
      p75_pts + (max_points - p75_pts) * ((perf - perf_p75) / (1 - perf_p75)),
    );
  }

  function makeRow(result_min: number, result_max: number, points: number): ScoreTableRow {
    return { result_min, result_max, points };
  }

  const rows: ScoreTableRow[] = [];

  if (lo > RESULT_MIN_CLAMP) {
    rows.push(makeRow(RESULT_MIN_CLAMP, lo - 1, pointsFor(lo - 1)));
  }

  let bandStart = lo;
  let bandPoints = pointsFor(lo);
  for (let r = lo + 1; r <= hi; r++) {
    const pts = pointsFor(r);
    if (pts !== bandPoints) {
      rows.push(makeRow(bandStart, r - 1, bandPoints));
      bandStart = r;
      bandPoints = pts;
    }
  }
  rows.push(makeRow(bandStart, hi, bandPoints));

  rows.push(makeRow(hi + 1, RESULT_MAX_CLAMP, pointsFor(hi + 1)));

  return rows;
}
